import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  FieldTimeOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  RollbackOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { FormInstance } from "antd";
import { SelectList } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { SelectOps } from "components/generalComponents/selectComponent/model/SelectOpsModel";
import isEmpty from "lodash/isEmpty";
import isObject from "lodash/isObject";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { SimTypeEnum } from "utils/enums/SimTypeEnum";
import {
  TransactionStatusEnum,
  TransactionTypeEnum,
} from "utils/enums/TransactionEnum";
import { avoidDuplicate, getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

export class DataFormat {
  static transactionStatusOptions(localStrings: typeof strings) {
    return [
      { label: localStrings.GlobalLabels.All, value: "" },
      {
        label: localStrings.AppTransactionHistory.Status.Init,
        value: TransactionStatusEnum.Init,
        color: "#2563eb",
        icon: <InfoCircleOutlined className="text-xl text-blue-600" />,
      },
      {
        label: localStrings.AppTransactionHistory.Status.Process,
        value: TransactionStatusEnum.Proccess,
        color: "#8b5cf6",
        icon: <HistoryOutlined className="text-xl text-purple-500" />,
      },
      {
        label: localStrings.AppTransactionHistory.Status.Success,
        value: TransactionStatusEnum.Success,
        color: "#16a34a",
        icon: <CheckCircleOutlined className="text-xl text-green-600" />,
      },
      {
        label: localStrings.AppTransactionHistory.Status.Failed,
        value: TransactionStatusEnum.Failed,
        color: "#dc2626",
        icon: <CloseCircleOutlined className="text-xl text-red-600" />,
      },
      {
        label: localStrings.AppTransactionHistory.Status.Pending,
        value: TransactionStatusEnum.Pending,
        color: "#d97706",
        icon: <ClockCircleOutlined className="text-xl text-amber-600" />,
      },
      {
        label: localStrings.AppTransactionHistory.Status.PaymentSuccess,
        value: TransactionStatusEnum.PaymentSuccess,
        color: "#0891b2",
        icon: <CreditCardOutlined className="text-xl text-cyan-600" />,
      },
      {
        label: localStrings.AppTransactionHistory.Status.Timeout,
        value: TransactionStatusEnum.Timeout,
        color: "#ea580c",
        icon: <FieldTimeOutlined className="text-xl text-orange-600" />,
      },
      {
        label: localStrings.AppTransactionHistory.Status.Deleted,
        value: TransactionStatusEnum.Deleted,
        color: "#991b1b",
        icon: <DeleteOutlined className="text-xl text-red-800" />,
      },
      {
        label: localStrings.AppTransactionHistory.Status.Refund,
        value: TransactionStatusEnum.Refund,
        color: "#0d9488",
        icon: <RollbackOutlined className="text-xl text-teal-600" />,
      },
      {
        label: localStrings.AppTransactionHistory.Status.Reject,
        value: TransactionStatusEnum.Reject,
        color: "#be123c",
        icon: <StopOutlined className="text-xl text-rose-700" />,
      },
    ] as SelectList[];
  }

  static transactionTypeOptions(localStrings: typeof strings) {
    return [
      { label: localStrings.GlobalLabels.All, value: "" },
      {
        label: localStrings.AppTransactionHistory.Type.Normal,
        value: TransactionTypeEnum.Normal,
      },
      {
        label: localStrings.AppTransactionHistory.Type.User,
        value: TransactionTypeEnum.User,
      },
      {
        label: localStrings.AppTransactionHistory.Type.Partner,
        value: TransactionTypeEnum.Partner,
      },
    ] as SelectList[];
  }

  static simTypeOptions(
    localStrings: typeof strings,
    alterSimType: boolean = false,
  ) {
    return [
      { label: localStrings.GlobalLabels.All, value: "" },
      {
        label: localStrings.SimManagement.SimType.Physical,
        value:
          alterSimType === true
            ? SimTypeEnum.alterPhysical
            : SimTypeEnum.physical,
      },
      {
        label: localStrings.SimManagement.SimType.ESim,
        value: alterSimType === true ? SimTypeEnum.alterESim : SimTypeEnum.eSim,
      },
    ] as SelectList[];
  }

  static simNumberTypeOptions(localStrings: typeof strings) {
    return [
      { label: localStrings.GlobalLabels.All, value: "" },
      ...Object.entries(
        localStrings.AppTransactionHistory.Labels.NumberTypeOps,
      )?.map(([keys, vals]) => ({
        label: vals,
        value: keys,
      })),
    ];
  }

  static renderItem = (
    label: string,
    value: React.ReactNode,
    color = "black",
  ) => {
    return (
      <div className="flex gap-x-3 justify-between w-full my-2">
        <span className="font-[500] text-[#909090] min-w-[100px]">
          {label}:
        </span>
        <span
          style={{
            color: color,
            fontWeight: color !== "black" ? 500 : "",
            textAlign: "right",
          }}
          className="overflow-visible text-ellipsis"
        >
          {value && value !== "-" && value !== "Invalid Date"
            ? value
            : strings.GlobalLabels.NoInfo}
        </span>
      </div>
    );
  };

  static removeUndefined(obj: Record<string, unknown>) {
    Object.keys(obj).forEach((key) => {
      const v = obj[key];
      if (
        v === undefined ||
        v === null ||
        (Array.isArray(v) && isEmpty(v)) ||
        (isObject(v) && isEmpty(v)) ||
        (typeof v === "string" && v.trim() === "")
      ) {
        delete obj[key];
      }
    });
    return obj;
  }

  static getToken() {
    return localStorage.getItem(CacheKeyEnum.isSavedForNextLogin) === "true"
      ? localStorage.getItem(CacheKeyEnum.mainToken)
      : sessionStorage.getItem(CacheKeyEnum.mainToken);
  }

  static clickToCopy = async (value: string) =>
    await window?.navigator?.clipboard
      ?.writeText(value)
      .then(() =>
        getMessage(
          `${strings.GlobalMessage.SavedToClipboard} - ${value}`,
          3,
          "success",
        ),
      );

  static handleMultipleChange = (
    selected: Array<unknown>,
    filterKey: string,
    form: FormInstance,
    isSingleItem: boolean = false,
  ) => {
    if (selected?.includes("") && selected.length > 1) {
      const newValues = isEmpty(selected.at(-1))
        ? [""]
        : isSingleItem
          ? [selected?.at(-1)]
          : selected.filter((item) => !isEmpty(item));
      form.setFieldsValue({ [filterKey]: newValues });
    } else if (selected.includes("") && selected.length === 1) {
      form.setFieldsValue({ mySelect: [""] });
    } else if (selected.length === 0) {
      form.setFieldsValue({ [filterKey]: [""] });
    } else {
      form.setFieldsValue({
        [filterKey]: isSingleItem ? [selected?.at(-1)] : selected,
      });
    }
  };

  static removeArrayTypeValue = <T extends Object>(obj: T) => {
    Object?.entries(obj)?.forEach(([keys, vals]) => {
      if (Array.isArray(vals)) {
        delete obj[keys];
      } else {
        return;
      }
    });
    return obj;
  };

  static includeArray = <T extends unknown>(
    firstArr: Array<T>,
    secondArr: Array<T>,
  ) => {
    return !isEmpty(firstArr?.filter((child) => secondArr?.includes(child)))
      ? firstArr.filter(
          (child) => !isEmpty(child) && !secondArr?.includes(child),
        )
      : undefined;
  };

  static selectFilterOption = <T extends Object>(
    input: string,
    options: SelectOps & T,
    customKey?: Array<string>,
  ) => {
    let hasLabel = options?.label?.toLowerCase().includes(input.toLowerCase());
    let hasValue = options?.value.toString() === input.toString().toLowerCase();
    let hasCustome = !!customKey && !isEmpty(customKey);
    if (
      hasLabel ||
      hasValue ||
      (hasCustome &&
        customKey?.some((item) => options[item]?.includes(input.toLowerCase())))
    ) {
      return true;
    }
    return false;
  };

  static autoCorrectJSON = (jsonString: string): string => {
    let correctedJSON = jsonString
      .replace(/(?<=\{|\,)\s*([\w]+)\s*:/g, '"$1":')
      .replace(/(?<=:)\s*'([^']*)'\s*(?=,|\}|$)/g, ' "$1"');
    correctedJSON = correctedJSON.replace(/,\s*([\]}])/g, "$1");
    return correctedJSON;
  };

  static toMbAndHour = <T extends number>(
    unit: string,
    baseValue: T,
    newValue: T,
  ) => {
    if (unit === "mb" || unit === "gb") {
      let value = Math.round(((newValue as number) / 1000) * 200) / 200;
      return avoidDuplicate(baseValue, unit === "mb" ? value : newValue);
    } else {
      return unit === "hour" && newValue >= 1
        ? avoidDuplicate(baseValue, Math.round(newValue / 24))
        : newValue < 1 && unit === "hour"
          ? avoidDuplicate(baseValue, newValue)
          : newValue < 1 && unit === "day"
            ? avoidDuplicate(baseValue, Math.round(newValue * 24))
            : avoidDuplicate(baseValue, newValue);
    }
  };

  static prepareParamsForApi<T extends Object>(params: T): T {
    const copy = { ...params };
    DataFormat.removeArrayTypeValue(copy);
    return copy as unknown as T;
  }

  static serializeParams = (params: Record<string, any>): string => {
    const parts: string[] = [];
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v === undefined || v === null) return;
          parts.push(
            `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`,
          );
        });
      } else {
        parts.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
        );
      }
    });
    return parts.join("&");
  };

  static gbFormat = (mb: string) => {
    return (Number(mb) / 1024)?.toFixed(1);
  };
}
